import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { fromEvent, merge, tap } from 'rxjs';
import { exhaustMap, filter, map, takeUntil } from 'rxjs/operators';
import gsap from 'gsap';
import { FgbBaseModal } from '../fgb-modal-base';
import { CommonModule, DOCUMENT } from '@angular/common';

@Component({
  selector: 'fgb-bottom-menu-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bottom-menu-modal.component.html',
  styleUrl: './bottom-menu-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BottomMenuModalComponent extends FgbBaseModal implements AfterViewInit {
  @ViewChild('dragPointer', { static: true }) private dragpointer!: ElementRef<HTMLDivElement>;
  @Input() marginTop: number = 0;
  @Input() startY: number = 200;
  @Input() minHeight: number = 0;
  @Input() animate: boolean = true;
  @Input() draggable: boolean = true;
  @Output() draggingChange: EventEmitter<any> = new EventEmitter();
  translateY: number = 0;
  isDragging: boolean = false;
  showCloseButton: boolean = true;
  private renderer: Renderer2 = inject(Renderer2);
  private cd: ChangeDetectorRef = inject(ChangeDetectorRef);
  private document = inject(DOCUMENT);
  @ViewChild('overlay', { static: true }) overlay!: ElementRef<HTMLDivElement>;

  override ngAfterViewInit(): void {
    this.translateY = this.startY;
    this.cd.markForCheck();
    if (!this.animate) {
      const modal = this.modal.nativeElement;
      gsap.set(modal, {
        transform: `translateY(${this.translateY}px)`,
        onComplete: () => {
          if (this.draggable) {
            this._initializeDragEvents();
          }
        },
      });
      return;
    }
    this.open();
  }

  private _initializeDragEvents(): void {
    const mouseDown$ = merge(
      fromEvent(this.dragpointer.nativeElement, 'mousedown'),
      fromEvent(this.modal.nativeElement, 'touchstart'),
      fromEvent(this.dragpointer.nativeElement, 'touchstart')
    );
    const mouseMove$ = merge(
      fromEvent(this.modal.nativeElement, 'touchmove'),
      fromEvent(this.dragpointer.nativeElement, 'touchmove'),
      fromEvent(window, 'mousemove')
    ).pipe(
      tap((e) => {
        e.preventDefault();
      })
    );
    const mouseUp$ = merge(
      fromEvent(this.modal.nativeElement, 'touchend'),
      fromEvent(this.dragpointer.nativeElement, 'touchend'),
      fromEvent(window, 'mouseup')
    ).pipe(
      tap(() => {
        if (!this.isDragging) return;
        if (this.minHeight > 0) {
          const modal = this.modal.nativeElement;
          if (this.translateY >= this.minHeight / 2) {
            gsap.fromTo(
              modal,
              { transform: `translateY(${this.translateY}px)` },
              {
                transform: `translateY(${this.minHeight}px)`,
                ease: 'power2.out',
                duration: 0.2,
                onComplete: () => {
                  this.translateY = this.minHeight;
                  this.draggingChange.emit(this.translateY);
                  let body = this.modal.nativeElement.querySelector('.bottom-menu-modal-body') as HTMLElement;

                  gsap.to(body, {
                    scrollTop: 0,
                    duration: 0.2,
                    ease: 'power2.out',
                  });
                  this.cd.markForCheck();
                },
              }
            );
          } else if (this.translateY < this.minHeight / 2) {
            gsap.fromTo(
              modal,
              { transform: `translateY(${this.translateY}px)` },
              {
                transform: `translateY(${0}px)`,
                ease: 'power2.out',
                duration: 0.2,
                onComplete: () => {
                  this.translateY = 0;
                  this.draggingChange.emit(this.translateY);
                  this.cd.markForCheck();
                },
              }
            );
          }
        } else {
          const modal = this.modal.nativeElement;
          if (this.translateY >= window.innerHeight / 2) {
            gsap.fromTo(
              modal,
              { transform: `translateY(${this.translateY}px)` },
              {
                transform: `translateY(${window.innerHeight}px)`,
                ease: 'power2.out',
                duration: 0.2,
                onStart: () => {
                  this.showCloseButton = false;
                  this.cd.markForCheck();
                },
                onComplete: () => {
                  this.translateY = window.innerHeight;
                  this.draggingChange.emit(this.translateY);
                  this.closed.emit();
                  this.cd.markForCheck();
                },
              }
            );
          } else if (this.translateY < window.innerHeight / 2) {
            gsap.fromTo(
              modal,
              { transform: `translateY(${this.translateY}px)` },
              {
                transform: `translateY(${0}px)`,
                ease: 'power2.out',
                duration: 0.2,
                onComplete: () => {
                  this.translateY = 0;
                  this.draggingChange.emit(this.translateY);
                  this.cd.markForCheck();
                },
              }
            );
          }
        }
        this.isDragging = false;
      })
    );

    const isScrollableAndScrolled = (target: HTMLElement): boolean => {
      const scrollEl = this.modal.nativeElement;
      return scrollEl.contains(target) && scrollEl.scrollTop > 0;
    };

    mouseDown$
      .pipe(
        filter((start: any) => {
          const target = start.target as HTMLElement;
          const fromDragHandle = this.dragpointer.nativeElement.contains(target);
          const canDrag = this.translateY > 0 || fromDragHandle;
          if (this.translateY === 0 && isScrollableAndScrolled(target)) {
            return false;
          }

          return canDrag;
        }),
        exhaustMap((start: any) => {
          const startY = start instanceof TouchEvent ? start.touches[0].clientY : start.clientY;
          const startTranslateY = this.translateY;
          let dragged = false;
          let movedY = 0;

          return mouseMove$.pipe(
            map((move: any) => {
              this.isDragging = true;
              const moveY = move instanceof TouchEvent ? move.touches[0].clientY : move.clientY;
              movedY = moveY - startY;
              if (Math.abs(movedY) > 5) {
                this.isDragging = true;
                dragged = true;
              }
              return startTranslateY + movedY;
            }),
            takeUntil(
              mouseUp$.pipe(
                tap(() => {
                  if (dragged) {
                    start.preventDefault();
                  }
                })
              )
            )
          );
        })
      )
      .subscribe((dy) => {
        let deltaY = Math.min(Math.max(dy, 0), window.innerHeight);
        if (this.minHeight > 0) {
          deltaY = Math.min(Math.max(dy, 0), this.minHeight);
        }
        this.translateY = deltaY;
        this.draggingChange.emit(deltaY);

        this.renderer.setStyle(this.modal.nativeElement, 'transform', `translateY(${deltaY}px)`);
        this.cd.markForCheck();
      });
  }

  open(): void {
    this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    const modal = this.modal.nativeElement;
    gsap.to(modal, {
      transform: `translateY(${this.translateY}px)`,
      ease: 'power2.out',
      duration: 0.5,
      onStart: () => {
        gsap.fromTo(
          this.overlay.nativeElement,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.5,
            ease: 'power2.out',
          }
        );
      },
      onComplete: () => {
        if (this.draggable) {
          this._initializeDragEvents();
        }
      },
    });
  }

  close(done: () => void) {
    this.showCloseButton = false;
    this.renderer.removeStyle(this.document.body, 'overflow');
    if (!this.animate) {
      done();
      return;
    }
    const modal = this.modal.nativeElement;
    gsap.to(modal, {
      transform: `translateY(${100}%)`,
      ease: 'power2.out',
      duration: 0.5,

      onStart: () => {
        gsap.fromTo(
          this.overlay.nativeElement,
          {
            opacity: 1,
          },
          {
            opacity: 0,
            duration: 0.5,
            ease: 'power2.out',
          }
        );
      },
      onComplete: done,
    });
  }
}
